import { forwardRef } from 'react'
import type { SelectHTMLAttributes } from 'react'
import { cn } from '../../lib/utils'

export interface SelectOption {
  value: string | number
  label: string
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[]
  placeholder?: string
  error?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder, error, disabled, ...props }, ref) => {
    return (
      <select
        ref={ref}
        className={cn(
          // Base styles
          'w-full px-3 py-2 text-sm text-text-muted',
          'transition-colors duration-200',
          'appearance-none bg-no-repeat bg-right',
          // Dropdown arrow
          'bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20fill%3D%27none%27%20viewBox%3D%270%200%2020%2020%27%3E%3Cpath%20stroke%3D%27%23555555%27%20stroke-linecap%3D%27round%27%20stroke-linejoin%3D%27round%27%20stroke-width%3D%271.5%27%20d%3D%27m6%208%204%204%204-4%27%2F%3E%3C%2Fsvg%3E")]',
          'bg-[length:1.5rem_1.5rem] bg-[right_0.25rem_center] pr-8',
          // Mobile: full border
          'border border-border rounded-md',
          'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
          // Desktop: bottom-border only style
          'md:border-0 md:border-b md:rounded-none md:bg-transparent',
          'md:hover:bg-input-hover',
          'md:focus:border-b-2 md:focus:border-primary md:focus:ring-0 md:focus:bg-input-hover',
          // Error state
          error && 'border-danger md:border-danger focus:border-danger focus:ring-danger/20',
          // Disabled state
          disabled && 'bg-surface cursor-not-allowed opacity-50',
          className
        )}
        disabled={disabled}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }
)

Select.displayName = 'Select'

export default Select
